import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import JustifiedGallery from './JustifiedGallery';
import MasonryGallery from './MasonryGallery';
import MosaicGrid from './MosaicGrid';
import MobilePhotoSwiper from './MobilePhotoSwiper';
import ProgressiveImage from './ProgressiveImage';
import useIsMobile from '../utils/useIsMobile';

export default function PublicGallery() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [viewMode, setViewMode] = useState('single');
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [theme, setTheme] = useState('auto');
  const [showMobileViewer, setShowMobileViewer] = useState(false);
  const [mobileViewerIndex, setMobileViewerIndex] = useState(0);
  const isMobile = useIsMobile();
  const isViewerCompact = useIsMobile(1024);
  
  // Track events to the server
  const trackEvent = async (eventType, imageId = null, eventData = null) => {
    try {
      await fetch('/api/shares/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          event_type: eventType,
          image_id: imageId,
          event_data: eventData
        })
      });
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  };
  
  useEffect(() => { 
    fetch(`/api/shares/public/${token}`)
      .then(r => r.json())
      .then(data => {
        setData(data);
        // Set theme: prefer explicit share theme, else album theme, else auto
        if (data.album && data.link) {
          const linkTheme = data.link.theme || data.link.theme_override || 'auto';
          const albumTheme = data.album.theme || 'auto';
          const effective = linkTheme !== 'auto' ? linkTheme : (albumTheme || 'auto');
          setTheme(effective || 'auto');
        }
      })
      .catch(() => setData({ error: true })); 
  }, [token]);
  
  // Track page leave
  useEffect(() => {
    const handleBeforeUnload = () => {
      trackEvent('page_leave');
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  
  // Listen for system theme changes
  useEffect(() => {
    if (theme === 'auto' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        // Force a small state update to trigger re-render without changing theme
        setData(prev => ({ ...prev }));
      };
      
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      }
    }
  }, [theme]);
  
  // Keyboard navigation for lightbox - MUST be before any conditional returns
  useEffect(() => {
    if (!data || !data.album || !lightboxImage) return;
    
    const images = data.images || [];
    
    function handleKeyDown(e) {
      if (e.key === 'ArrowLeft') {
        const newIndex = (lightboxIndex - 1 + images.length) % images.length;
        setLightboxImage(images[newIndex]);
        setLightboxIndex(newIndex);
      }
      if (e.key === 'ArrowRight') {
        const newIndex = (lightboxIndex + 1) % images.length;
        setLightboxImage(images[newIndex]);
        setLightboxIndex(newIndex);
      }
      if (e.key === 'Escape') {
        setLightboxImage(null);
      }
    }
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage, lightboxIndex, data]);
  
  if (!data) {
    return (
      <div className="public-gallery-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  if (data.error || data.message) {
    return (
      <div className="public-gallery-error">
        <div className="error-card">
          <h2>Gallery Unavailable</h2>
          <p>{data.message || 'This gallery link may have expired or been removed.'}</p>
          <p className="muted">Please contact the photographer for a new link.</p>
        </div>
      </div>
    );
  }
  
  const { album, link, images } = data;
  
  // Determine effective theme
  const effectiveTheme = (() => {
    if (theme === 'auto') {
      // Check system preference
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
  })();
  
  function sharePreview(image) {
    const tile = document.querySelector(`[data-image-id="${CSS.escape(String(image.id))}"] img`);
    return tile?.complete && tile.naturalWidth ? tile.currentSrc || tile.src : undefined;
  }

  function openLightbox(image, index) {
    image = { ...image, _previewSrc: sharePreview(image) };
    if (isViewerCompact) {
      setMobileViewerIndex(index);
      setShowMobileViewer(true);
    } else {
      setLightboxImage(image);
      setLightboxIndex(index);
    }
    trackEvent('lightbox_open', image.id);
    trackEvent('photo_view', image.id);
  }
  
  function closeLightbox() {
    setLightboxImage(null);
    trackEvent('lightbox_close');
  }
  
  function navigateLightbox(direction) {
    const newIndex = (lightboxIndex + direction + images.length) % images.length;
    setLightboxImage(images[newIndex]);
    setLightboxIndex(newIndex);
    trackEvent('photo_view', images[newIndex].id);
  }
  
  // Touch/swipe handlers
  function handleTouchStart(e) {
    setTouchStart(e.targetTouches[0].clientX);
  }
  
  function handleTouchMove(e) {
    setTouchEnd(e.targetTouches[0].clientX);
  }
  
  function handleTouchEnd() {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      navigateLightbox(1); // Next image
    } else if (isRightSwipe) {
      navigateLightbox(-1); // Previous image
    }
  }
  
  return (
    <div className="public-gallery" data-theme={effectiveTheme}>
      <header className="public-gallery-header">
        <div className="gallery-info">
          <h1>{album.title}</h1>
          {album.description && <p className="gallery-description">{album.description}</p>}
          <p className="gallery-count muted">{images.length} photos</p>
        </div>
        {link.allow_downloads && (
          <div className="gallery-actions">
            <a 
              className="download-album-button"
              href={`/api/shares/public/${link.token}/download`}
              onClick={() => trackEvent('download', null, { type: 'album_zip', count: images.length })}
            >
              Download Album (ZIP)
            </a>
          </div>
        )}
      </header>
      
      {/* Relaxed masonry layout: natural aspect ratios, no crop, fluid heights */}
      <MasonryGallery
        photos={images}
        shareToken={token}
        watermark={link.watermark}
        gap={isMobile ? 6 : 8}
        minColumnWidth={isMobile ? 160 : 280}
        maxColumns={isMobile ? 2 : 4}
        onPhotoClick={openLightbox}
      />
      
      {lightboxImage && (
        <div className="lightbox">
          <div className="lightbox-header">
            <div className="lightbox-controls">
              <div className="lightbox-view-toggle">
                <button 
                  className={`view-toggle-btn ${viewMode === 'single' ? 'active' : ''}`}
                  onClick={() => setViewMode('single')}
                >
                  Single
                </button>
                <button 
                  className={`view-toggle-btn ${viewMode === 'contact' ? 'active' : ''}`}
                  onClick={() => setViewMode('contact')}
                >
                  Gallery
                </button>
              </div>
              {link.allow_downloads && viewMode === 'single' && (
                <a 
                  href={`/media/share/original/${lightboxImage.filename}?token=${token}&download=1`}
                  download={lightboxImage.filename}
                  className="download-button-header"
                  onClick={(e) => { e.stopPropagation(); trackEvent('download', lightboxImage.id, { type: 'single_image' }); }}
                >
                  Download
                </a>
              )}
            </div>
            <button className="lightbox-close" onClick={closeLightbox}>
              ✕
            </button>
          </div>

          {viewMode === 'single' ? (
            <div className="lightbox-single">
              <button 
                className="lightbox-nav lightbox-prev" 
                onClick={() => navigateLightbox(-1)}
              >
                ‹
              </button>
              
              <div 
                className="lightbox-image-container"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <ProgressiveImage
                  identity={`${token}:${lightboxImage.id}`}
                  previewSrc={lightboxImage._previewSrc || `/media/share/md/${lightboxImage.filename.replace(/\.[^.]+$/, '.jpg')}?token=${token}`}
                  className="lightbox-image"
                  src={`/media/share/lg/${lightboxImage.filename.replace(/\.[^.]+$/, '.jpg')}?token=${token}`}
                  alt={lightboxImage.title || 'Photo'}
                />
                
                {(link.show_titles || lightboxImage.caption) && (
                  <div className="lightbox-info">
                    {link.show_titles && lightboxImage.title && <h3>{lightboxImage.title}</h3>}
                    {lightboxImage.caption && <p>{lightboxImage.caption}</p>}
                    <div className="lightbox-meta">
                      <span className="muted">#{lightboxIndex + 1} of {images.length}</span>
                    </div>
                  </div>
                )}
              </div>
              
              <button 
                className="lightbox-nav lightbox-next" 
                onClick={() => navigateLightbox(1)}
              >
                ›
              </button>
            </div>
          ) : (
            <div className="lightbox-contact-sheet">
              <div className="contact-sheet-grid">
                {images.map((img, index) => (
                  <div 
                    key={img.id}
                    className={`contact-sheet-item ${lightboxImage.id === img.id ? 'active' : ''}`}
                    onClick={() => {
                      setLightboxImage(img);
                      setLightboxIndex(index);
                      setViewMode('single');
                    }}
                  >
                    <span className="contact-sheet-index">#{index + 1}</span>
                    <img 
                      src={`/media/share/sm/${img.filename.replace(/\.[^.]+$/, '.jpg')}?token=${token}`}
                      alt={img.title || 'Photo'}
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Mobile Photo Viewer (Swiper) for shares */}
      {showMobileViewer && images && (
        <MobilePhotoSwiper
          images={images.map((img) => ({
            ...img,
            preview_url: sharePreview(img) || `/media/share/md/${img.filename.replace(/\.[^.]+$/, '.jpg')}?token=${token}`,
            // Use share URL with watermark if configured
            url: link.watermark ?
              `/media/share/lg/${img.filename.replace(/\.[^.]+$/, '.jpg')}?token=${token}` :
              `/media/share/lg/${img.filename.replace(/\.[^.]+$/, '.jpg')}?token=${token}`
          }))}
          initialIndex={mobileViewerIndex}
          onClose={() => {
            setShowMobileViewer(false);
            trackEvent('lightbox_close');
          }}
          showActions={link.allow_downloads}
          allowDownload={link.allow_downloads}
        />
      )}
    </div>
  );
}
